import { apiClient } from '../api/client'
import { API_ENDPOINTS } from '../api/endpoints'
import { secureStorage } from '../utils/secureStorage'
import { getCurrentApiUrl } from './apiConfig'
import { generateStableDeviceId, getMachineName } from '../utils/hardwareId'

export { encryptString, generateHardDiskId, generateStableDeviceId, getMachineName } from '../utils/hardwareId'

/**
 * توليد معرف فريد للجهاز — ثابت بعد أول إنشاء (لا يتغير مع Refresh).
 */
export function generateDeviceId(): string {
  return generateStableDeviceId()
}

/**
 * توليد Device Fingerprint (معلومات إضافية للتعريف)
 */
export function generateDeviceFingerprint(): string {
  const fingerprint = {
    userAgent: navigator.userAgent,
    language: navigator.language,
    languages: navigator.languages?.join(',') || '',
    platform: navigator.platform,
    screenResolution: `${screen.width}x${screen.height}`,
    colorDepth: screen.colorDepth,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    cookieEnabled: navigator.cookieEnabled,
    onLine: navigator.onLine,
    hardwareConcurrency: navigator.hardwareConcurrency || 0,
    deviceMemory: (navigator as any).deviceMemory || 0,
  }

  return JSON.stringify(fingerprint)
}

/**
 * يفضّل اسم الترخيص/الشركة على "Windows - Chrome" عند التسجيل
 */
export function resolvePreferredDeviceName(osBrowserFallback: string): string {
  try {
    const authRaw = localStorage.getItem('auth-storage') || sessionStorage.getItem('auth-storage')
    if (authRaw) {
      const parsed = JSON.parse(authRaw)
      const companyName = parsed?.state?.user?.companyName
      if (typeof companyName === 'string' && companyName.trim()) {
        return companyName.trim()
      }
    }
  } catch {
    // ignore
  }

  return osBrowserFallback
}

/**
 * جمع معلومات الجهاز
 */
export function collectDeviceInfo() {
  const deviceId = generateDeviceId()
  const deviceFingerprint = generateDeviceFingerprint()
  
  // استخراج اسم المتصفح
  const userAgent = navigator.userAgent
  let browserInfo = 'Unknown'
  if (userAgent.includes('Chrome')) browserInfo = 'Chrome'
  else if (userAgent.includes('Firefox')) browserInfo = 'Firefox'
  else if (userAgent.includes('Safari')) browserInfo = 'Safari'
  else if (userAgent.includes('Edge')) browserInfo = 'Edge'
  else if (userAgent.includes('Opera')) browserInfo = 'Opera'

  // استخراج نظام التشغيل
  let operatingSystem = 'Unknown'
  if (userAgent.includes('Windows')) operatingSystem = 'Windows'
  else if (userAgent.includes('Mac')) operatingSystem = 'macOS'
  else if (userAgent.includes('Linux')) operatingSystem = 'Linux'
  else if (userAgent.includes('Android')) operatingSystem = 'Android'
  else if (userAgent.includes('iOS')) operatingSystem = 'iOS'

  const osBrowserName = `${operatingSystem} - ${browserInfo}`

  return {
    deviceId,
    deviceName: resolvePreferredDeviceName(osBrowserName),
    deviceFingerprint,
    operatingSystem,
    browserInfo: `${browserInfo} ${userAgent.match(/(?:Chrome|Firefox|Safari|Edge|Opera)\/(\d+)/)?.[1] || ''}`,
    screenResolution: `${screen.width}x${screen.height}`,
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    language: navigator.language,
  }
}

/**
 * تسجيل الجهاز عند أول تشغيل
 * إذا فشل الاتصال، يعرض نافذة لإدخال API URL
 */
export async function registerDevice(): Promise<{ isFirstLaunch: boolean; deviceId: string; needsApiConfig?: boolean }> {
  try {
    console.log('📱 Starting device registration process...')
    const deviceInfo = collectDeviceInfo()
    console.log('📱 Collected device info:', { deviceId: deviceInfo.deviceId, deviceName: deviceInfo.deviceName })
    
    // التحقق من أول تشغيل
    const isFirstLaunch = !secureStorage.getItem('deviceRegistered')
    console.log('📱 Is first launch?', isFirstLaunch)
    
    const response = await apiClient.post(API_ENDPOINTS.deviceRegistration.register, {
      deviceId: deviceInfo.deviceId,
      deviceName: deviceInfo.deviceName,
      deviceFingerprint: deviceInfo.deviceFingerprint,
      operatingSystem: deviceInfo.operatingSystem,
      browserInfo: deviceInfo.browserInfo,
      screenResolution: deviceInfo.screenResolution,
      timeZone: deviceInfo.timeZone,
      language: deviceInfo.language,
      additionalInfo: JSON.stringify({
        apiBaseUrl: getCurrentApiUrl()
      })
    })

    console.log('📱 Backend response:', response.data)

    if (response.data.success) {
      // حفظ علامة التسجيل
      secureStorage.setItem('deviceRegistered', 'true')
      secureStorage.setItem('deviceId', deviceInfo.deviceId)
      
      if (response.data.isFirstLaunch) {
        console.log('✅ Device registered successfully (First Launch)')
      } else {
        console.log('✅ Device already registered, last seen updated')
      }
    } else {
      console.warn('⚠️ Device registration failed:', response.data.message)
    }

    return {
      isFirstLaunch: response.data.isFirstLaunch || false,
      deviceId: deviceInfo.deviceId,
    }
  } catch (error: any) {
    console.error('❌ Error registering device:', error)
    
    // إذا كان الخطأ بسبب عدم الاتصال بالإنترنت أو CORS أو عنوان خاطئ، نطلب تكوين API
    if (
      error.code === 'ERR_NETWORK' ||
      error.message?.includes('Failed to fetch') ||
      error.message?.includes('CORS') ||
      !error.response ||
      error.response?.status === 0 ||
      error.response?.status === 404 ||
      error.response?.status === 502 ||
      error.response?.status === 503
    ) {
      console.warn('🌐 Connection error detected, requesting API config')
      return {
        isFirstLaunch: false,
        deviceId: generateDeviceId(),
        needsApiConfig: true, // يحتاج إلى تكوين API URL
      }
    }
    
    // لا نرمي خطأ حتى لا يعطل التطبيق
    return {
      isFirstLaunch: false,
      deviceId: generateDeviceId(),
    }
  }
}

/**
 * التحقق من تسجيل الجهاز
 */
export async function checkDeviceRegistration(deviceId: string): Promise<boolean> {
  try {
    const response = await apiClient.get(API_ENDPOINTS.deviceRegistration.check(deviceId))
    return response.data.isRegistered || false
  } catch (error) {
    console.error('Error checking device registration:', error)
    return false
  }
}

/**
 * تحديث آخر ظهور للجهاز
 */
export async function updateLastSeen(deviceId: string): Promise<void> {
  try {
    await apiClient.post(API_ENDPOINTS.deviceRegistration.updateLastSeen(deviceId))
  } catch (error) {
    // لا نرمي خطأ حتى لا يعطل التطبيق
    console.error('Error updating last seen:', error)
  }
}

